import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { createTrackedChangesHtml, countChanges, extractCleanContent } from '../../utils/richDiff';
import '../../styles/ampDescriptionDisplay.css';

const RERENDER_DELAY = 600; // ms after last keystroke before re-rendering tracked changes

// ── Cursor save/restore utilities ──────────────────────────────────
// We measure cursor position as a character offset over editable text
// only (skipping <del> elements which are contenteditable="false").

function getCaretCharOffset(element) {
  const sel = window.getSelection();
  if (!sel.rangeCount || !element.contains(sel.anchorNode)) return -1;
  try {
    const range = sel.getRangeAt(0);
    const pre = range.cloneRange();
    pre.selectNodeContents(element);
    pre.setEnd(range.startContainer, range.startOffset);
    const frag = pre.cloneContents();
    // Remove <del> content — it's non-editable so the cursor is never inside it
    frag.querySelectorAll('del').forEach(el => el.remove());
    return frag.textContent.length;
  } catch {
    return -1;
  }
}

function setCaretCharOffset(element, offset) {
  if (offset < 0) return;
  try {
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) =>
        node.parentElement?.closest('del')
          ? NodeFilter.FILTER_REJECT
          : NodeFilter.FILTER_ACCEPT
    });
    let n = 0;
    let node;
    while ((node = walker.nextNode())) {
      const len = node.textContent.length;
      if (n + len >= offset) {
        const r = document.createRange();
        r.setStart(node, Math.min(offset - n, len));
        r.collapse(true);
        const s = window.getSelection();
        s.removeAllRanges();
        s.addRange(r);
        return;
      }
      n += len;
    }
    // Past end → place cursor at end
    const r = document.createRange();
    r.selectNodeContents(element);
    r.collapse(false);
    const s = window.getSelection();
    s.removeAllRanges();
    s.addRange(r);
  } catch {
    /* best effort */
  }
}

// ── DOM helpers for intercepted deletion handling ──────────────────
// These let us convert characters to <del> elements instantly on
// Backspace / Delete, avoiding the "disappear then reappear" flash
// that the debounced re-diff causes.

function indexOfChild(child, parent) {
  const children = parent.childNodes;
  for (let i = 0; i < children.length; i++) {
    if (children[i] === child) return i;
  }
  return -1;
}

/** Walk backward through the DOM tree to find the first text node + charIndex
 *  that is NOT inside a <del>. Returns { textNode, charIndex, isIns } or null. */
function findCharBefore(container, node, offset) {
  let cur = node;
  let pos = offset;

  for (let safety = 0; safety < 80; safety++) {
    // 1. Text node with pos > 0 → character is right here
    if (cur.nodeType === Node.TEXT_NODE && pos > 0) {
      if (cur.parentElement?.closest('del.tc-del')) {
        // inside a <del> — skip backwards past its boundary
        const delEl = cur.parentElement.closest('del.tc-del');
        pos = indexOfChild(delEl, delEl.parentNode);
        cur = delEl.parentNode;
        continue;
      }
      return {
        textNode: cur,
        charIndex: pos - 1,
        isIns: !!cur.parentElement?.closest('ins.tc-ins')
      };
    }

    // 2. Determine the "previous thing" to look at
    let prev;
    if (cur.nodeType === Node.TEXT_NODE) {
      prev = cur.previousSibling;
      if (!prev) {
        if (cur.parentNode && cur.parentNode !== container) {
          pos = indexOfChild(cur, cur.parentNode);
          cur = cur.parentNode;
          continue;
        }
        return null;
      }
    } else if (pos > 0) {
      prev = cur.childNodes[pos - 1];
    } else {
      // pos === 0 in element → go up
      if (cur.parentNode && cur.parentNode !== container) {
        pos = indexOfChild(cur, cur.parentNode);
        cur = cur.parentNode;
        continue;
      }
      return null;
    }

    if (!prev) return null;

    // 3. Skip <del> elements
    if (prev.nodeName === 'DEL' && prev.classList?.contains('tc-del')) {
      pos = indexOfChild(prev, prev.parentNode);
      cur = prev.parentNode;
      continue;
    }

    // 4. Descend into prev to find its last text character
    const last = getLastTextNode(prev);
    if (last && last.textContent.length > 0) {
      return {
        textNode: last,
        charIndex: last.textContent.length - 1,
        isIns: !!last.parentElement?.closest('ins.tc-ins')
      };
    }

    // prev is empty → keep looking further back
    pos = indexOfChild(prev, prev.parentNode);
    cur = prev.parentNode;
  }
  return null;
}

/** Walk forward to find the next deletable character. */
function findCharAfter(container, node, offset) {
  let cur = node;
  let pos = offset;

  for (let safety = 0; safety < 80; safety++) {
    if (cur.nodeType === Node.TEXT_NODE && pos < cur.textContent.length) {
      if (cur.parentElement?.closest('del.tc-del')) {
        const delEl = cur.parentElement.closest('del.tc-del');
        pos = indexOfChild(delEl, delEl.parentNode) + 1;
        cur = delEl.parentNode;
        continue;
      }
      return {
        textNode: cur,
        charIndex: pos,
        isIns: !!cur.parentElement?.closest('ins.tc-ins')
      };
    }

    let next;
    if (cur.nodeType === Node.TEXT_NODE) {
      next = cur.nextSibling;
      if (!next) {
        if (cur.parentNode && cur.parentNode !== container) {
          pos = indexOfChild(cur, cur.parentNode) + 1;
          cur = cur.parentNode;
          continue;
        }
        return null;
      }
    } else if (pos < cur.childNodes.length) {
      next = cur.childNodes[pos];
    } else {
      if (cur.parentNode && cur.parentNode !== container) {
        pos = indexOfChild(cur, cur.parentNode) + 1;
        cur = cur.parentNode;
        continue;
      }
      return null;
    }

    if (!next) return null;

    if (next.nodeName === 'DEL' && next.classList?.contains('tc-del')) {
      pos = indexOfChild(next, next.parentNode) + 1;
      cur = next.parentNode;
      continue;
    }

    const first = getFirstTextNode(next);
    if (first && first.textContent.length > 0) {
      return {
        textNode: first,
        charIndex: 0,
        isIns: !!first.parentElement?.closest('ins.tc-ins')
      };
    }

    pos = indexOfChild(next, next.parentNode) + 1;
    cur = next.parentNode;
  }
  return null;
}

function getLastTextNode(node) {
  if (node.nodeType === Node.TEXT_NODE) return node;
  if (node.nodeName === 'DEL' && node.classList?.contains('tc-del')) return null;
  for (let i = node.childNodes.length - 1; i >= 0; i--) {
    const r = getLastTextNode(node.childNodes[i]);
    if (r) return r;
  }
  return null;
}

function getFirstTextNode(node) {
  if (node.nodeType === Node.TEXT_NODE) return node;
  if (node.nodeName === 'DEL' && node.classList?.contains('tc-del')) return null;
  for (let i = 0; i < node.childNodes.length; i++) {
    const r = getFirstTextNode(node.childNodes[i]);
    if (r) return r;
  }
  return null;
}

/** Merge adjacent <del class="tc-del"> siblings into one element. */
function mergeAdjacentDels(del) {
  if (!del || !del.parentNode) return del;
  while (del.previousSibling?.nodeName === 'DEL' && del.previousSibling.classList?.contains('tc-del')) {
    const prev = del.previousSibling;
    del.textContent = prev.textContent + del.textContent;
    prev.remove();
  }
  while (del.nextSibling?.nodeName === 'DEL' && del.nextSibling.classList?.contains('tc-del')) {
    const nxt = del.nextSibling;
    del.textContent = del.textContent + nxt.textContent;
    nxt.remove();
  }
  return del;
}

/** Convert one character at textNode[charIndex] into a <del> element.
 *  `forward` controls cursor placement (before vs after the <del>). */
function convertCharToDel(textNode, charIndex, forward) {
  const ch = textNode.textContent.charAt(charIndex);
  if (!ch) return;

  const before = textNode.textContent.substring(0, charIndex);
  const after  = textNode.textContent.substring(charIndex + 1);

  const del = document.createElement('del');
  del.className = 'tc-del';
  del.setAttribute('contenteditable', 'false');
  del.textContent = ch;

  const parent = textNode.parentNode;

  if (before && after) {
    textNode.textContent = before;
    const afterNode = document.createTextNode(after);
    parent.insertBefore(del, textNode.nextSibling);
    parent.insertBefore(afterNode, del.nextSibling);
  } else if (before) {
    textNode.textContent = before;
    parent.insertBefore(del, textNode.nextSibling);
  } else if (after) {
    textNode.textContent = after;
    parent.insertBefore(del, textNode);
  } else {
    parent.insertBefore(del, textNode);
    parent.removeChild(textNode);
  }

  mergeAdjacentDels(del);

  // Position cursor
  const sel = window.getSelection();
  const r = document.createRange();
  if (forward) {
    // Delete key → cursor stays in place (after the <del>)
    const nxt = del.nextSibling;
    if (nxt && nxt.nodeType === Node.TEXT_NODE) {
      r.setStart(nxt, 0);
    } else {
      r.setStartAfter(del);
    }
  } else {
    // Backspace → cursor before the <del>
    const prv = del.previousSibling;
    if (prv && prv.nodeType === Node.TEXT_NODE) {
      r.setStart(prv, prv.textContent.length);
    } else {
      r.setStartBefore(del);
    }
  }
  r.collapse(true);
  sel.removeAllRanges();
  sel.addRange(r);
}

/** Actually remove one character from an <ins> element (deleting an addition). */
function deleteInsChar(textNode, charIndex) {
  const before = textNode.textContent.substring(0, charIndex);
  const after  = textNode.textContent.substring(charIndex + 1);

  const sel = window.getSelection();
  const rng = document.createRange();

  if (before || after) {
    textNode.textContent = before + after;
    rng.setStart(textNode, before.length);
  } else {
    // Last char in text node → maybe remove the <ins> wrapper
    const insEl = textNode.parentElement?.closest('ins.tc-ins');
    const parent = textNode.parentNode;
    textNode.remove();
    if (insEl && !insEl.textContent) {
      const nxt = insEl.nextSibling;
      const prv = insEl.previousSibling;
      insEl.remove();
      if (nxt && nxt.nodeType === Node.TEXT_NODE) {
        rng.setStart(nxt, 0);
      } else if (prv && prv.nodeType === Node.TEXT_NODE) {
        rng.setStart(prv, prv.textContent.length);
      } else {
        rng.setStart(parent, 0);
      }
    } else {
      rng.setStart(parent, 0);
    }
  }
  rng.collapse(true);
  sel.removeAllRanges();
  sel.addRange(rng);
}

/** Handle a selection-based deletion (user highlighted text then pressed delete). */
function handleSelectionDeletion(sel, container) {
  const range = sel.getRangeAt(0);

  // If the entire selection is inside an <ins>, just remove it
  const ancestor = range.commonAncestorContainer;
  const insParent = ancestor.nodeType === Node.TEXT_NODE
    ? ancestor.parentElement?.closest('ins.tc-ins')
    : ancestor.closest?.('ins.tc-ins');

  if (insParent && insParent.contains(range.startContainer) && insParent.contains(range.endContainer)) {
    range.deleteContents();
    if (!insParent.textContent) insParent.remove();
    return;
  }

  // General case: extract original (non-del, non-ins) text, convert to <del>
  const frag = range.cloneContents();
  frag.querySelectorAll('del.tc-del').forEach(d => d.remove());
  frag.querySelectorAll('ins.tc-ins').forEach(ins => ins.remove());
  const originalText = frag.textContent;

  range.deleteContents();

  if (originalText) {
    const del = document.createElement('del');
    del.className = 'tc-del';
    del.setAttribute('contenteditable', 'false');
    del.textContent = originalText;
    range.insertNode(del);

    const r = document.createRange();
    r.setStartAfter(del);
    r.collapse(true);
    sel.removeAllRanges();
    sel.addRange(r);

    mergeAdjacentDels(del);
  }
}

/**
 * TrackedChangesView — Renders a formatted HTML document with inline
 * tracked changes (MS Word-style). Additions are highlighted in green,
 * removals are shown with red strikethrough, all within the formatted content.
 *
 * When `editable` is true, the reviewer can type directly in the tracked
 * changes view. Edits are re-diffed against the original content with a
 * short debounce so additions and removals appear inline in real-time.
 *
 * Props:
 *   oldContent — the previous/original HTML content
 *   newContent — the proposed/new HTML content
 *   style      — optional container style overrides
 *   label      — optional label above the view (e.g. "Reviewer's Changes")
 *   editable   — if true, enables inline editing within the track changes view
 *   onChange   — callback(cleanHtml) fired when the reviewer edits content
 */
const TrackedChangesView = ({ oldContent, newContent, style, label, editable, onChange }) => {
  const containerRef = useRef(null);
  const prevTrackedRef = useRef('');
  const debounceRef = useRef(null);

  const [displayContent, setDisplayContent] = useState(newContent);
  const [isEditing, setIsEditing] = useState(false);

  // Sync displayContent when parent changes newContent (e.g., switching revisions)
  useEffect(() => {
    setDisplayContent(newContent);
  }, [newContent]);

  const trackedHtml = useMemo(
    () => createTrackedChangesHtml(oldContent || '', displayContent || ''),
    [oldContent, displayContent]
  );

  const changes = useMemo(() => countChanges(trackedHtml), [trackedHtml]);
  const hasChanges = changes.additions > 0 || changes.removals > 0;

  // Set innerHTML via ref when trackedHtml changes (initial render + state-driven updates)
  useEffect(() => {
    if (editable && containerRef.current && trackedHtml !== prevTrackedRef.current) {
      containerRef.current.innerHTML = trackedHtml;
      prevTrackedRef.current = trackedHtml;
    }
  }, [editable, trackedHtml]);

  // Re-render tracked changes in-place, preserving cursor position.
  // Returns the clean HTML or null if the container isn't available.
  const rerenderTrackedChanges = useCallback(() => {
    if (!containerRef.current) return null;

    const hasFocus = document.activeElement === containerRef.current;
    const caretOffset = hasFocus ? getCaretCharOffset(containerRef.current) : -1;

    const cleanContent = extractCleanContent(containerRef.current.innerHTML);
    const newTracked = createTrackedChangesHtml(oldContent || '', cleanContent);

    containerRef.current.innerHTML = newTracked;
    prevTrackedRef.current = newTracked;

    if (hasFocus) setCaretCharOffset(containerRef.current, caretOffset);

    setDisplayContent(cleanContent);
    return cleanContent;
  }, [oldContent]);

  const handleInput = useCallback(() => {
    setIsEditing(true);

    // Notify parent immediately with clean content
    if (containerRef.current && onChange) {
      onChange(extractCleanContent(containerRef.current.innerHTML));
    }

    // Debounced re-render → shows the moderator's edits as tracked changes
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const clean = rerenderTrackedChanges();
      if (clean !== null && onChange) onChange(clean);
    }, RERENDER_DELAY);
  }, [onChange, rerenderTrackedChanges]);

  const handleBlur = useCallback(() => {
    // Cancel any pending debounce and do an immediate re-render
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    setIsEditing(false);
    const clean = rerenderTrackedChanges();
    if (clean !== null && onChange) onChange(clean);
  }, [onChange, rerenderTrackedChanges]);

  // Helper: notify parent + schedule debounced re-render after a manual DOM edit
  const afterManualEdit = useCallback(() => {
    if (containerRef.current && onChange) {
      onChange(extractCleanContent(containerRef.current.innerHTML));
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const clean = rerenderTrackedChanges();
      if (clean !== null && onChange) onChange(clean);
    }, RERENDER_DELAY);
  }, [onChange, rerenderTrackedChanges]);

  // Intercept Backspace / Delete to convert characters to <del> instantly
  // instead of letting the browser delete them (which causes a flash when
  // the debounced re-diff re-inserts them as <del> 600ms later).
  const handleKeyDown = useCallback((e) => {
    if (!editable || !containerRef.current) return;
    const key = e.key;
    if (key !== 'Backspace' && key !== 'Delete') return;

    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;

    const container = containerRef.current;
    const isBackspace = key === 'Backspace';

    // ── Selection-based deletion ──
    if (!sel.isCollapsed) {
      e.preventDefault();
      handleSelectionDeletion(sel, container);
      setIsEditing(true);
      afterManualEdit();
      return;
    }

    // ── Word (Option) or line (Cmd) deletion ──
    if (e.altKey || e.metaKey) {
      e.preventDefault();
      const isLine = e.metaKey;
      let count = 0;
      let hitNonWhitespace = false;

      while (count < 500) {
        const range = sel.getRangeAt(0);
        const info = isBackspace
          ? findCharBefore(container, range.startContainer, range.startOffset)
          : findCharAfter(container, range.startContainer, range.startOffset);
        if (!info) break;

        const ch = info.textNode.textContent.charAt(info.charIndex);

        // Word delete: stop at word boundary (whitespace after non-ws)
        if (!isLine) {
          const isWs = /\s/.test(ch);
          if (hitNonWhitespace && isWs) break;
          if (!isWs) hitNonWhitespace = true;
        }

        // Line delete: stop at newline
        if (isLine && ch === '\n') break;

        if (info.isIns) {
          deleteInsChar(info.textNode, info.charIndex);
        } else {
          convertCharToDel(info.textNode, info.charIndex, !isBackspace);
        }
        count++;
      }
      if (count > 0) {
        setIsEditing(true);
        afterManualEdit();
      }
      return;
    }

    // ── Single character deletion ──
    const range = sel.getRangeAt(0);
    const info = isBackspace
      ? findCharBefore(container, range.startContainer, range.startOffset)
      : findCharAfter(container, range.startContainer, range.startOffset);

    if (!info) {
      e.preventDefault(); // at boundary — nothing to delete
      return;
    }

    e.preventDefault();

    if (info.isIns) {
      deleteInsChar(info.textNode, info.charIndex);
    } else {
      convertCharToDel(info.textNode, info.charIndex, !isBackspace);
    }

    setIsEditing(true);
    afterManualEdit();
  }, [editable, afterManualEdit]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Paste as plain text to avoid formatting pollution
  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  }, []);

  // Shared content styles
  const contentStyle = {
    fontSize: '0.95rem',
    lineHeight: '1.6',
    padding: '1rem 1.25rem',
    background: '#fafafa',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    maxHeight: '400px',
    overflowY: 'auto',
    outline: 'none',
    ...(editable ? { cursor: 'text', border: '1px solid #93c5fd' } : {})
  };

  return (
    <div style={style}>
      {/* Header with legend */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '0.5rem',
        flexWrap: 'wrap',
        gap: '0.5rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {label && (
            <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>
              {label}
            </div>
          )}
          {editable && isEditing && (
            <span style={{
              fontSize: '0.7rem',
              color: '#6b7280',
              fontStyle: 'italic'
            }}>
              editing...
            </span>
          )}
        </div>
        {hasChanges && (
          <div style={{
            display: 'flex',
            gap: '0.75rem',
            fontSize: '0.75rem',
            color: '#6b7280',
            fontWeight: 600,
            marginLeft: 'auto'
          }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{
                display: 'inline-block',
                width: '10px',
                height: '10px',
                background: '#dcfce7',
                border: '1px solid #86efac',
                borderRadius: '2px'
              }} />
              {changes.additions} added
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{
                display: 'inline-block',
                width: '10px',
                height: '10px',
                background: '#fee2e2',
                border: '1px solid #fecaca',
                borderRadius: '2px'
              }} />
              {changes.removals} removed
            </span>
          </div>
        )}
      </div>

      {/* Tracked changes document */}
      {editable ? (
        <div
          ref={containerRef}
          className="amp-description-display tracked-changes-view tracked-changes-editable"
          contentEditable
          suppressContentEditableWarning
          onKeyDown={handleKeyDown}
          onInput={handleInput}
          onBlur={handleBlur}
          onPaste={handlePaste}
          style={contentStyle}
        />
      ) : (
        <div
          className="amp-description-display tracked-changes-view"
          style={contentStyle}
          dangerouslySetInnerHTML={{ __html: trackedHtml }}
        />
      )}

      {editable && (
        <div style={{
          fontSize: '0.7rem',
          color: '#9ca3af',
          marginTop: '0.3rem',
          fontStyle: 'italic'
        }}>
          Type to edit — your changes are tracked automatically as additions and removals.
        </div>
      )}

      {!hasChanges && !editable && (
        <div style={{
          padding: '0.75rem 1rem',
          color: '#6b7280',
          fontStyle: 'italic',
          fontSize: '0.85rem',
          textAlign: 'center'
        }}>
          No text changes detected.
        </div>
      )}
    </div>
  );
};

export default TrackedChangesView;
